<template>
  <div class="bg-white dark-bg-custom h-full flex flex-col">
    <div class="flex-grow overflow-y-auto p-4 custom-scrollbar">
      <!-- Year and Regime Selection -->
      <div class="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <v-select
          v-model="selectedYear"
          :items="yearOptions"
          label="Select Financial Year"
          outlined
          dense
          hide-details
          @change="fetchDeclarations"
        ></v-select>

        <div class="flex items-center md:justify-end" v-if="taxData">
          <v-chip
            :color="taxData.taxRegime === 'new' ? 'success' : 'primary'"
            text-color="white"
            class="font-weight-bold"
          >
            Regime: {{ taxData.taxRegime === 'new' ? 'New Tax Regime' : 'Old Tax Regime' }}
          </v-chip>
        </div>
      </div>

      <div v-if="loading" class="flex justify-center items-center h-40">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <div v-else-if="taxData">
        <!-- Summary Cards if needed -->

        <!-- Signed Form Section -->
        <div
          v-if="taxData.signedFormUrl"
          class="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between"
        >
          <div class="flex items-center gap-3">
            <div class="bg-blue-100 p-2 rounded-full">
              <v-icon color="blue darken-1">mdi-file-document-check-outline</v-icon>
            </div>
            <div>
              <h3 class="text-md font-semibold text-blue-900">Signed Form 12BB</h3>
              <p class="text-xs text-blue-700 mb-0">
                Review the signed tax declaration form submitted by the employee.
              </p>
            </div>
          </div>
          <v-btn color="primary" outlined small @click="viewSignedForm">
            <v-icon left small>mdi-eye</v-icon>
            View Form
          </v-btn>
        </div>

        <!-- Sections -->

        <div v-for="(group, sectionName) in groupedDeclarations" :key="sectionName" class="mb-6">
          <div class="flex items-center mb-3 justify-between">
            <div class="flex items-center">
              <v-icon color="primary" class="mr-2">mdi-folder-open</v-icon>
              <h3 class="text-lg font-bold text-gray-700">Section {{ sectionName }}</h3>
            </div>
            <div
              class="bg-blue-50 text-blue-700 px-3 py-1 rounded font-semibold text-sm border border-blue-100"
            >
              Total: ₹{{ formatCurrency(getSectionTotal(group)) }}
            </div>
          </div>

          <v-expansion-panels
            flat
            tile
            multiple
            v-model="expandedPanels[sectionName]"
            class="border rounded-lg overflow-hidden"
          >
            <v-expansion-panel
              v-for="declaration in group"
              :key="declaration._id"
              class="border-b last:border-b-0"
            >
              <v-expansion-panel-header class="bg-gray-50 hover:bg-gray-100">
                <div class="flex justify-between items-center w-full mr-4">
                  <div class="flex flex-col">
                    <span class="font-semibold text-gray-800">{{
                      getDeductionLabel(declaration.deductionKey)
                    }}</span>
                    <span
                      class="text-xs text-gray-500 mt-1"
                      v-if="declaration.investments.length > 0"
                    >
                      {{ declaration.investments.length }} Investment(s)
                    </span>
                  </div>
                  <div class="flex items-center gap-4">
                    <span class="font-bold text-primary"
                      >₹{{ formatCurrency(getTotalAmount(declaration.investments)) }}</span
                    >
                    <!-- <v-chip x-small :color="getOverallStatusColor(declaration.investments)">
                      {{ getOverallStatus(declaration.investments) }}
                    </v-chip> -->
                  </div>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content class="pt-4">
                <div
                  v-if="declaration.investments.length === 0"
                  class="text-center py-4 text-gray-400"
                >
                  No investments declared.
                </div>

                <div v-else class="space-y-4">
                  <div
                    v-for="investment in declaration.investments"
                    :key="investment._id"
                    class="p-4 border rounded-lg bg-gray-50"
                  >
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-3">
                      <div>
                        <span class="text-xs text-gray-500 block">Institution</span>
                        <span class="font-medium text-sm">{{ investment.institution || '-' }}</span>
                      </div>
                      <div>
                        <span class="text-xs text-gray-500 block">Reference No.</span>
                        <span class="font-medium text-sm">{{
                          investment.referenceNumber || '-'
                        }}</span>
                      </div>
                      <div class="text-right">
                        <span class="text-xs text-gray-500 block">Amount</span>
                        <span class="font-medium text-sm"
                          >₹{{ formatCurrency(investment.amount) }}</span
                        >
                      </div>
                      <div class="col-span-full" v-if="investment.description">
                        <span class="text-xs text-gray-500 block">Description</span>
                        <span class="font-medium text-sm">{{ investment.description }}</span>
                      </div>

                      <!-- Extra Details -->
                      <template v-if="investment.extraDetails">
                        <div v-for="(val, key) in investment.extraDetails" :key="key">
                          <span class="text-xs text-gray-500 block">{{
                            formatCamelCase(key)
                          }}</span>
                          <span class="font-medium text-sm">{{ val }}</span>
                        </div>
                      </template>
                    </div>

                    <v-divider class="mb-3"></v-divider>

                    <!-- Proofs Section -->
                    <div
                      class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
                    >
                      <div class="flex items-center gap-2">
                        <div
                          v-if="investment.proofs && investment.proofs.length > 0"
                          class="flex gap-2"
                        >
                          <v-chip
                            v-for="(proof, pIdx) in investment.proofs"
                            :key="pIdx"
                            small
                            outlined
                            color="primary"
                            @click="viewProof(proof)"
                          >
                            <v-icon left x-small>mdi-paperclip</v-icon>
                            {{ truncateFileName(proof.name) || 'Proof ' + (pIdx + 1) }}
                          </v-chip>
                        </div>
                        <span v-else class="text-xs text-gray-500 italic">No proofs attached</span>
                      </div>

                      <div class="flex gap-2 pl-4 border-l ml-2">
                        <v-file-input
                          v-model="uploadFiles[`${declaration._id}-${investment._id}`]"
                          hide-details
                          dense
                          outlined
                          small-chips
                          label="Attach Proof"
                          prepend-icon=""
                          prepend-inner-icon="mdi-paperclip"
                          class="max-w-[200px] text-xs"
                          height="32"
                          @change="handleFileUpload($event, declaration._id, investment._id)"
                        ></v-file-input>

                        <v-btn
                          color="primary"
                          class="mt-1"
                          small
                          depressed
                          :disabled="!uploadFiles[`${declaration._id}-${investment._id}`]"
                          :loading="uploading[`${declaration._id}-${investment._id}`]"
                          @click="uploadProof(declaration._id, investment._id)"
                        >
                          <v-icon small>mdi-upload</v-icon>
                        </v-btn>
                      </div>

                      <div class="flex gap-2 items-center justify-end">
                        <template v-if="investment.status === 'PENDING'">
                          <v-btn
                            small
                            color="success"
                            class="white--text"
                            depressed
                            :loading="
                              updatingStatus[`${declaration._id}-${investment._id}-APPROVED`]
                            "
                            @click="updateStatus(declaration._id, investment._id, 'APPROVED')"
                          >
                            <v-icon left small>mdi-check</v-icon> Approve
                          </v-btn>
                          <v-btn
                            small
                            color="error"
                            depressed
                            outlined
                            :loading="
                              updatingStatus[`${declaration._id}-${investment._id}-REJECTED`]
                            "
                            @click="updateStatus(declaration._id, investment._id, 'REJECTED')"
                          >
                            <v-icon left small>mdi-close</v-icon> Reject
                          </v-btn>
                        </template>
                        <v-chip
                          v-else
                          small
                          :color="getStatusColor(investment.status)"
                          text-color="white"
                          label
                        >
                          {{ investment.status }}
                        </v-chip>
                      </div>
                    </div>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </div>

      <div v-else class="text-center py-10">
        <v-icon size="64" color="gray lighten-2">mdi-file-document-outline</v-icon>
        <p class="text-gray-500 mt-2">No tax declaration data found for this year.</p>
      </div>
    </div>

    <!-- Image Viewer Dialog -->
    <v-dialog v-model="viewerDialog" max-width="900" scrollable>
      <v-card class="rounded-lg overflow-hidden flex flex-col h-[60vh]">
        <div class="">
          <h2
            class="text-subtitle-1 font-weight-bold text-gray-800 truncate p-4 flex justify-between"
          >
            {{ viewerTitle || 'Document Preview' }}
            <v-btn icon small @click="viewerDialog = false" class="ml-2">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </h2>
        </div>

        <v-card-text
          class="pa-0 flex-grow-1 bg-gray-100 relative bg-pattern"
          :class="{ 'flex items-center justify-center': viewerType !== 'pdf' }"
        >
          <!-- PDF Viewer: Full Width/Height, No Padding -->
          <div v-if="viewerType === 'pdf'" class="w-full h-full">
            <iframe
              :src="viewerUrl"
              class="w-full h-full shadow-none bg-white"
              frameborder="0"
            ></iframe>
          </div>

          <!-- Image Viewer: Centered with Padding -->
          <div v-else class="w-full h-full overflow-auto flex items-center justify-center p-4">
            <img
              :src="viewerUrl"
              class="max-w-full max-h-full object-contain shadow-lg rounded bg-white"
              alt="Proof Document"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'TaxDeclarations',
  props: {
    employeeData: {
      type: Object,
      required: true
    }
  },
  data() {
    const today = new Date()
    const currentMonth = today.getMonth() // 0-11
    const currentYear = today.getFullYear()
    // If Jan, Feb, Mar -> previous year is FY Start
    const financialYearStart = currentMonth < 3 ? currentYear - 1 : currentYear

    return {
      loading: false,
      selectedYear: financialYearStart,
      taxData: null,
      uploadFiles: {},
      expandedPanels: {},
      uploading: {},
      updatingStatus: {},
      viewerDialog: false,
      viewerUrl: '',
      viewerTitle: '',
      viewerType: 'image',
      s3BaseUrl: process.env.VUE_APP_S3_BASE_URL,
      yearOptions: [
        { text: `${financialYearStart}-${financialYearStart + 1}`, value: financialYearStart },
        { text: `${financialYearStart - 1}-${financialYearStart}`, value: financialYearStart - 1 },
        {
          text: `${financialYearStart - 2}-${financialYearStart - 1}`,
          value: financialYearStart - 2
        }
      ]
    }
  },
  computed: {
    groupedDeclarations() {
      if (!this.taxData || !this.taxData.taxDeclarations) return {}

      // Group by Section
      return this.taxData.taxDeclarations.reduce((groups, item) => {
        const section = item.section || 'Other'
        if (!groups[section]) {
          groups[section] = []
        }
        groups[section].push(item)
        return groups
      }, {})
    }
  },
  watch: {
    employeeData: {
      handler(val) {
        if (val && val._id) {
          this.fetchDeclarations()
        }
      },
      immediate: true
    },
    groupedDeclarations: {
      handler(val) {
        const panels = {}
        if (val) {
          Object.keys(val).forEach((section) => {
            panels[section] = val[section].map((_, i) => i)
          })
        }
        this.expandedPanels = panels
      },
      immediate: true
    }
  },
  methods: {
    async fetchDeclarations() {
      if (!this.employeeData?._id) return

      this.loading = true
      try {
        await new Promise((resolve) => setTimeout(resolve, 800))

        try {
          const response = await this.$axios.get(
            `/employee/employee-tax/${this.employeeData._id}/${this.selectedYear}`
          )
          if (response.data && response.data.success) {
            this.taxData = response.data.employeeTax // Adjust based on actual API response structure
          } else {
            // Fallback or empty
            this.taxData = null
          }
        } catch (apiError) {
          console.warn(
            'API fetch failed, falling back to demo data for visualization if applicable',
            apiError
          )
        }
      } catch (error) {
        console.error('Error fetching tax declarations:', error)
        this.$toast.error('Failed to load tax declarations')
      } finally {
        this.loading = false
      }
    },

    getDeductionLabel(key) {
      if (!key) return '-'
      return key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    },

    formatCamelCase(text) {
      if (!text) return ''
      return text.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())
    },

    formatCurrency(val) {
      return (val || 0).toLocaleString('en-IN')
    },

    getTotalAmount(investments) {
      return (investments || []).reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0)
    },

    getSectionTotal(group) {
      return group.reduce((sum, dec) => sum + this.getTotalAmount(dec.investments), 0)
    },

    getStatusColor(status) {
      switch (status) {
        case 'APPROVED':
          return 'success'
        case 'REJECTED':
          return 'error'
        case 'PENDING':
          return 'warning'
        default:
          return 'grey'
      }
    },

    handleFileUpload(file, declarationId, investmentId) {
      // Just needed to trigger the reactivity or store the file
      if (file) {
        this.$set(this.uploadFiles, `${declarationId}-${investmentId}`, file)
      }
    },

    async uploadProof(declarationId, investmentId) {
      const file = this.uploadFiles[`${declarationId}-${investmentId}`]
      if (!file) return

      const key = `${declarationId}-${investmentId}`
      this.$set(this.uploading, key, true)

      try {
        const formData = new FormData()
        formData.append('proof', file)
        formData.append('declarationId', declarationId)
        formData.append('investmentId', investmentId)

        // await this.$axios.post('/employee/tax-declaration/upload-proof', formData);

        await new Promise((resolve) => setTimeout(resolve, 1500)) // Simulating upload

        this.$toast.success('Proof uploaded successfully')
        this.$set(this.uploadFiles, key, null) // Clear file input

        // Ideally update local state to show proof attached or refresh
        // Finding the investment and adding a mock proof
        this.taxData.taxDeclarations.forEach((dec) => {
          if (dec._id === declarationId) {
            dec.investments.forEach((inv) => {
              if (inv._id === investmentId) {
                inv.proofs.push('mock-url-to-proof.jpg')
              }
            })
          }
        })
      } catch (error) {
        console.error('Upload failed', error)
        this.$toast.error('Failed to upload proof')
      } finally {
        this.$set(this.uploading, key, false)
      }
    },

    async updateStatus(declarationId, investmentId, status) {
      if (!this.taxData || !this.taxData._id) {
        this.$toast.error('Employee Tax record not found')
        return
      }

      const key = `${declarationId}-${investmentId}-${status}`
      this.$set(this.updatingStatus, key, true)

      try {
        await this.$axios.put(
          `/employee/employee-tax/${this.taxData._id}/investment/${investmentId}/status`,
          { status }
        )

        // Update local state
        const declaration = this.taxData.taxDeclarations.find((d) => d._id === declarationId)
        if (declaration) {
          const investment = declaration.investments.find((i) => i._id === investmentId)
          if (investment) {
            investment.status = status
          }
        }

        this.$toast.success(`Investment ${status.toLowerCase()} successfully`)
      } catch (error) {
        console.error('Status update failed', error)
        this.$toast.error('Failed to update status')
      } finally {
        this.$set(this.updatingStatus, key, false)
      }
    },

    viewSignedForm() {
      if (!this.taxData || !this.taxData.signedFormUrl) return

      const url = this.getProofUrl({ url: this.taxData.signedFormUrl })
      if (!url) return

      this.viewerUrl = url
      this.viewerTitle = 'Signed Form 12BB'
      this.viewerType = this.getFileType(url)
      this.viewerDialog = true
    },

    getProofUrl(proof) {
      if (!proof || !proof.url) return ''
      if (proof.url.startsWith('http')) return proof.url
      // Append to S3 base URL if relative
      const baseUrl = this.s3BaseUrl || ''
      return `${baseUrl}${proof.url}`
    },

    viewProof(proof) {
      const url = this.getProofUrl(proof)
      if (!url) return

      this.viewerUrl = url
      this.viewerTitle = proof.name
      this.viewerType = this.getFileType(url)
      this.viewerDialog = true
    },

    getFileType(url) {
      if (!url) return 'image'
      return url.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image'
    },

    truncateFileName(name) {
      if (!name) return ''
      return name.length > 20 ? name.substring(0, 17) + '...' : name
    }
  }
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #bdbdbd;
}
.bg-pattern {
  background-image: radial-gradient(#e5e7eb 1px, transparent 1px);
  background-size: 20px 20px;
}
</style>
